'use client'

import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/hooks/use-auth'
import { fitsPlanLimit, normalizePlanLimit } from '@/lib/billing/utils/plan-limits'

export interface PbxNumberUsageState {
  count: number
  /** null = unlimited */
  limit: number | null
  loading: boolean
  /**
   * True when the tenant has no headroom for another number. Drives the
   * disable state on the «Connect to flow» button in `PhoneNumbersTab` for
   * phones that aren't already configured. The server-side entitlement gate
   * (POST `/api/pbx/phones/[phoneNumber]/enable`) is still authoritative;
   * this is a UX shortcut to avoid 4xx round-trips.
   *
   * Fail-open semantics: when the usage fetch fails (transient 500, network
   * blip, 403 on a permission-restricted user), `atLimit` stays `false` and
   * the UI defers to the server gate. Treating a fetch error as "at limit"
   * would block legitimate configurations on infrastructure hiccups.
   */
  atLimit: boolean
  refresh: () => Promise<void>
}

interface InternalState {
  count: number
  limit: number | null
  loading: boolean
  /** Tracks whether the most recent fetch returned usable data. */
  loadedOk: boolean
}

const INITIAL: InternalState = {
  count: 0,
  limit: 0,
  loading: true,
  loadedOk: false,
}

export function usePbxNumberUsage(): PbxNumberUsageState {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [state, setState] = useState<InternalState>(INITIAL)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const res = await fetchWithAuth('/api/billing/pbx-number-usage')
      if (!res.ok) {
        setState({ count: 0, limit: 0, loading: false, loadedOk: false })
        return
      }
      const data = (await res.json()) as { currentCount?: number; limit?: number | null }
      setState({
        count: typeof data.currentCount === 'number' ? data.currentCount : 0,
        limit: normalizePlanLimit(data.limit),
        loading: false,
        loadedOk: true,
      })
    } catch {
      setState({ count: 0, limit: 0, loading: false, loadedOk: false })
    }
  }, [fetchWithAuth, isAuthenticated])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Blocks at or over the cap (including a 0 limit), but only after a successful
  // load: a fetch error must not fail closed, the server gate stays authoritative.
  const atLimit = !state.loading && state.loadedOk && !fitsPlanLimit(state.count + 1, state.limit)

  return {
    count: state.count,
    limit: state.limit,
    loading: state.loading,
    atLimit,
    refresh,
  }
}
